import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import clsx from "clsx";
import update from "immutability-helper";

import closeEye from "../../../../images/icons/closeEye.png";
import openEye from "../../../../images/icons/openEye.png";
import {getCurrentTemplate} from "../../../../slices/KPSlice";
import COF_ASSET from "../../../../utilities/assets/COF";
import REQUEST_ASSET from "../../../../utilities/assets/REQUEST";
import SKU_PLAN_ASSET from "../../../../utilities/assets/SKU_PLAN";
import SKU_PLAN_LIST_ASSET from "../../../../utilities/assets/SKU_PLAN_LIST";
import SP_ASSET from "../../../../utilities/assets/SP";
import IconButton from "../../../../utilities/Buttons/IconButton";
import ImageButton from "../../../../utilities/Buttons/ImageBtn";
import {ICON_NAMES} from "../../../../utilities/generalConstants.js/generalConstants";
import {moneyFormat, numberFormat} from "../../../../utilities/moneyFormat";
import IndeterminateCheckbox from "../../../conteiners/reactTable/IndeterminateCheckbox";
import {selectCurrentSkuRequest} from "../../../Logist/redux/selectors";
import {getRequestInfo} from "../../../Requests/RequestsSlice";
import {getCurrentSkuPlan, getPlanSkuList, selectAllCOfServices, selectCurrentCof} from "../KpFinSlice";

import {pairedSkuPlanColumns} from "./KpFinTable";
import KpFinTableRow from "./KpFinTableRow";

const useKpFinTableService = (
  defaultColumns = [],
  dataRaw,
  onReorder,
  onSelect,
  scrollToBottom = true,
  moveRows = true,
  rowCheck = false,
  onRowCheck,
  frozeChecked,
  setIsSaved = () => {},
  onlySkuView = false,
  isForTemplates = false,
  isForRequest = false,
  isForStockPool = false,
  onColumnsChange = () => {},
  hasRejectedSkus,
  kpReadOnly,
  showPaymentSummaryRows = true,
  linkImageColumns = true,
) => {
  const {
    t,
    i18n: {language},
  } = useTranslation();

  const [selectedRow, setSelectedRow] = useState(null);
  const [columns, setColumns] = useState(defaultColumns || []);

  useEffect(() => {
    setColumns(defaultColumns || []);
  }, [defaultColumns]);

  const hiddenColumnsString = columns
    ?.filter(({isDisabled}) => isDisabled)
    ?.map(({accessorKey}) => accessorKey)
    .join(",");

  const cof = useSelector(selectCurrentCof);
  const advanceForServices = cof?.advanceForServices;
  const cofServices = useSelector(selectAllCOfServices);
  // const allServicesCost = cofServices?.reduce((acc, {serviceCost}) => acc + +serviceCost, 0) || 0;

  const [data, setData] = useState([]);
  const [disabledColls, setDisabledColls] = useState([]);
  const [hiddenRows, setHiddenRows] = useState([]);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [checkedRows, setCheckedRows] = useState({});

  const [totalSumm, setTotalSumm] = useState(null);

  const planSkuList = useSelector(getPlanSkuList);
  const currentSkuPlan = useSelector(getCurrentSkuPlan);
  const currentTemplate = useSelector(getCurrentTemplate);
  const skuTemplates = currentTemplate?.skuTemplates;
  const tableRef = useRef(null);

  const currentRequest = useSelector(getRequestInfo);
  const requestInfo = currentRequest?.requestInfo;
  const REQUEST = new REQUEST_ASSET(currentRequest, language);

  const currentStockPoolRequest = useSelector(selectCurrentSkuRequest);
  const SP = new SP_ASSET(currentStockPoolRequest);

  const services = isForRequest ? requestInfo?.servicesInfo : cofServices;

  const COF = new COF_ASSET(cof, cofServices);
  const SKU_PLAN_LIST = new SKU_PLAN_LIST_ASSET(
    isForTemplates && skuTemplates ? skuTemplates : planSkuList,
    cofServices,
    advanceForServices,
  );

  useEffect(() => {
    setColumns(defaultColumns);
    setDisabledColls((defaultColumns || []).filter(c => c?.isDisabled).map(c => c.accessorKey));
  }, [defaultColumns]);

  useEffect(() => {
    setData(dataRaw);
    if (scrollToBottom) setShouldScrollToBottom(true);
    if (dataRaw && dataRaw.length > 0) setTotalSumm(calculateTotalSumm(dataRaw));
    if (!dataRaw || dataRaw.length === 0) setTotalSumm(null);
  }, [dataRaw]);

  useEffect(() => {
    if (shouldScrollToBottom && tableRef.current) {
      const tableBody = tableRef.current.querySelector("tbody");
      tableBody.scrollTop = tableBody.scrollHeight;
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom]);

  useEffect(() => {
    // if (defaultSelection?.lenght === 0) setRowSelection({});
    if (frozeChecked?.length > 0 && data.length > 0) {
      var selection = {};
      data.forEach(row => {
        if (frozeChecked.includes(row.id)) {
          selection[row.number - 1] = true;
        }
      });
      setCheckedRows(selection);
    } else if (rowCheck && Object.keys(checkedRows).length) {
      setCheckedRows({});
    }
  }, [data, frozeChecked]);

  useEffect(() => {
    if (!currentSkuPlan) setSelectedRow(null);
  }, [currentSkuPlan?.skuPlanId]);

  const reorderRow = useCallback(
    (draggedRowIndex, targetRowIndex) => {
      const draggedRow = data[draggedRowIndex];
      const updatedData = update(data, {
        $splice: [
          [draggedRowIndex, 1],
          [targetRowIndex, 0, draggedRow],
        ],
      });

      updatedData.forEach((row, index) => {
        row.number = index + 1;
      });

      onReorder(updatedData);
      setShouldScrollToBottom(true);
      setData(updatedData);
    },
    [data, onReorder],
  );

  const calculateTotalSumm = () => {
    let bareSum = 0;
    // const totalSumm = data.reduce((accumulator, currentValue) => {
    //   const number = Number(currentValue.paymentSumm);
    //   discountSum += Number.parseFloat(currentValue.price) * Number.parseFloat(currentValue.amount) - number;
    //   bareSum += Number.parseFloat(currentValue.price) * Number.parseFloat(currentValue.amount);
    //   if (!isNaN(number)) {
    //     return Number(accumulator) + number;
    //   }
    //   return Number(accumulator);
    // }, 0);

    const totalSumm = planSkuList?.reduce((accumulator, skuPlan) => {
      const SKU_PLAN = new SKU_PLAN_ASSET(skuPlan, language);
      // discountSum += SKU_PLAN.finalPricePerObject * SKU_PLAN.amount;
      bareSum += SKU_PLAN.totalPrice;

      return accumulator + +SKU_PLAN.paymentSum;
    }, 0);

    const discountSum = +bareSum + -totalSumm;

    return {
      totalSumm, //: numberFormat(totalSumm),
      discountSum: discountSum === 0 ? "0.00" : numberFormat(discountSum),
      bareSum, //: numberFormat(bareSum),
    };
  };

  const getRowId = useCallback(row => {
    return row.id;
  }, []);

  const defaultColumn = useMemo(
    () => ({
      size: 140,
      minSize: 35,
      maxSize: 420,
      minWidth: 35,
      width: 140,
      maxWidth: 420,
    }),
    [],
  );

  const table = useReactTable(
    {
      columns,
      data,
      defaultColumn,
      enableColumnResizing: true,
      enableRowSelection: true,
      columnResizeMode: "onChange",
      getCoreRowModel: getCoreRowModel(),
      getRowId: row => row.userId,
      // debugTable: true,
      // debugHeaders: true,
      // debugColumns: true,

      state: {
        rowSelection: checkedRows,
      },
      onRowSelectionChange: setCheckedRows,
    },
    getRowId,
  );

  useEffect(() => {
    //!Important for redux state management on checkbox change
    const selectedIds = Object.keys(checkedRows).map(id => {
      return table.getRow(id).original.id;
    });

    if (onRowCheck) {
      onRowCheck(selectedIds);
    }
  }, [checkedRows]);

  const toggleColumn = column => {
    if (!column) return;
    setIsSaved(false);

    const isImageColumn = column.id === "image1" || column.id === "image2";
    const pair = isImageColumn ? null : pairedSkuPlanColumns?.[column.id];

    if (disabledColls.includes(column.id)) {
      setDisabledColls(coll => coll.filter(c => (pair ? c !== column.id || c !== pair : c !== column.id)));
    } else {
      setDisabledColls(coll => (pair ? [...coll, column.id, pair] : [...coll, column.id]));
    }

    const updatedColumns = columns.map(col => {
      if (col.accessorKey === column.id || (pair && col.accessorKey === pair)) {
        return {
          ...col,
          isDisabled: !col.isDisabled,
        };
      }

      return col;
    });
    setColumns(updatedColumns);
    onColumnsChange(updatedColumns);
  };

  const handleRowClick = row => {
    if (selectedRow === row && selectedRow !== null) {
      setSelectedRow(null);
      // dispatch(deselect());
      onSelect(row.original.id);

      return;
    }
    onSelect(row.original.id);
    setSelectedRow(row);
  };

  const checkCollWidth = () => {
    return table.getHeaderGroups()[0].headers.map(coll => {
      return coll.getSize();
    });
  };

  const checkFontSize = () => {
    const element = document.getElementById("checkFontSize");
    if (!element) return "11";
    const computed = window.getComputedStyle(element);
    const fontSize = computed && computed.fontSize ? computed.fontSize : null;
    if (!fontSize) return "11";
    const parsed = parseFloat(fontSize);
    if (Number.isFinite(parsed)) return String(parsed);
    return "11";
  };

  const generateDataForExport = (text, servicesAndDiscounts, header, pdfLanguage = language) => {
    var headerRow = [];
    var ww = checkCollWidth();

    const pdfHeaderKeyByAccessor = {
      brandName: "Brand w*",
      skuName: "Nomenclature",
      characteristics: "Characteristics",
      ovh: "VWC",
      image1: "Image",
      image2: "Image",
      complectDesc: "Set description",
      measurUnits: "Measur units",
      price: "Price (€)",
      discount: "Discount %",
      summ: "Sum € ",
      topDiscount: "Total discount %",
      totalSumm: "Total € ",
      amount: "Quantity",
      paymentSumm: "Payment €",
    };

    const columnsWithNoView = columns.filter(({noView}) => noView).map(({accessorKey}) => accessorKey);
    const hiddenColumns = [...new Set([...(Array.isArray(disabledColls) ? disabledColls : []), ...columnsWithNoView])];

    const headerKeys = [];

    const fontSize = checkFontSize();

    let charCollDisabled = false;

    let collSpanBeforeCharCell = -1;
    const beforeCharCellKeys = [];

    columns.some(coll => {
      if (coll.accessorKey !== "characteristics") {
        collSpanBeforeCharCell++;
        beforeCharCellKeys.push(coll.accessorKey);
        // return coll.accessorKey;
      }
      return coll.accessorKey === "characteristics";
    });

    var newWW = [];
    const numberFormatAccessors = [];

    columns.forEach((coll, index) => {
      if (index === 0) return;

      if (!disabledColls.includes(coll.accessorKey) && !columnsWithNoView.includes(coll.accessorKey)) {
        const rawHeader = coll.headerName || (typeof coll.header === "function" && coll.header()) || coll.header;
        // || typeof coll.header === "function" ? coll.header() : coll.header;
        const headerText = Array.isArray(rawHeader) ? rawHeader[0] : rawHeader;

        const keyForPdf = pdfHeaderKeyByAccessor[coll.accessorKey];
        headerRow.push(keyForPdf ? t(keyForPdf, {lng: pdfLanguage}) : headerText);

        headerKeys.push(coll.accessorKey);

        newWW.push(ww[index]);
      } else {
        if (coll.accessorKey === "characteristics") {
          charCollDisabled = true;
        }
        if (beforeCharCellKeys.includes(coll.accessorKey)) {
          collSpanBeforeCharCell--;
        }
      }
      if (coll.numberFormat) {
        numberFormatAccessors.push(coll.accessorKey);
      }
    });

    const dataRows = [];
    const rowImages = [];
    const headerStringData = [];
    const productGroupNames = [];
    const materialTypes = [];
    const colorTypes = [];

    const typeCells = charCollDisabled ? null : [];

    const summRow = `${t("Total to be paid", {lng: pdfLanguage})} ${moneyFormat(totalSumm?.totalSumm)}`;

    data.forEach(item => {
      const {
        // id,
        overallView,
        decorImages,
        colorImages,
        headerData,
        // number,
        decorText,
        colorText,
        productGroupName,
        ...restItem
      } = item;

      const row = [];

      // for (var key in restItem) {
      //   if (disabledColls.includes(key) || columnsWithNoView.includes(key)) {
      //     continue;
      //   }
      //   if (key === "image1") {
      //     const images1 = [];
      //     if (restItem[key]) images1.push(restItem[key]);
      //     row.push(images1.length ? images1 : "");
      //   } else if (key === "image2") {
      //     const images2 = [];
      //     if (restItem[key]) images2.push(restItem[key]);
      //     row.push(images2.length ? images2 : "");
      //   } else if (key === "isPriceOutRange") {
      //     continue;
      //   } else if (numberFormatAccessors.includes(key)) {
      //     row.push(numberFormat(restItem[key]));
      //   } else {
      //     row.push(restItem[key] || "");
      //   }
      // }

      defaultColumns?.forEach(({accessorKey: key}, index) => {
        if (index === 0) return;
        if (disabledColls.includes(key) || columnsWithNoView.includes(key)) {
          return;
        }
        if (key === "image1") {
          const images1 = [];
          if (restItem[key]) images1.push(restItem[key]);
          row.push(images1.length ? images1 : "");
        } else if (key === "image2") {
          const images2 = [];
          if (restItem[key]) images2.push(restItem[key]);
          row.push(images2.length ? images2 : "");
        } else if (key === "isPriceOutRange") {
          return;
        } else if (numberFormatAccessors.includes(key)) {
          row.push(numberFormat(restItem[key]));
        } else {
          row.push(restItem[key] || "");
        }
      });

      if (!charCollDisabled) {
        typeCells.push({decorType: decorText, colorType: colorText});
      }

      rowImages.push({overallView, decorImages, colorImages});
      headerStringData.push(headerData);
      dataRows.push(row);
      productGroupNames.push(productGroupName || "");
      materialTypes.push(decorText || "");
      colorTypes.push(colorText || "");
    });

    return {
      header, //верхний стандарный хедер с логотипом, контактами
      headerRow,
      headerKeys,
      hiddenColumns,
      typeCells,
      rowImages,
      dataRows,
      productGroupNames,
      materialTypes,
      colorTypes,
      headerStringData,
      summRow,
      text: text,
      // widths: adjustTableWidths(newWW, 690),
      fontSize: fontSize,
      widths: newWW,
      collSpanBeforeCharCell,
      servicesAndDiscounts,
      summRowData: totalSumm,
    };
  };

  const onToggleShowRowBtnClick = rowName => {
    setHiddenRows(prev => (prev?.includes(rowName) ? prev?.filter(item => item !== rowName) : [...prev, rowName]));
  };

  const KpFinTableRender = () => {
    return (
      <div
        style={{
          height: "100%",
          // width: "297mm",
          // width: "11.7in",
        }}
        className="project_list_kp table_dnd_container "
      >
        <DndProvider backend={HTML5Backend}>
          <table
            className="project_table fixedLayout"
            ref={tableRef}
            style={{
              // width: "297mm",
              fontSize: "11px",
            }}
          >
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {moveRows && <th style={{width: "2em"}}></th>}
                  {rowCheck && (
                    <th
                      key={"i0"}
                      style={{
                        position: "relative",
                        width: 25,
                      }}
                    >
                      <IndeterminateCheckbox
                        {...{
                          checked: table.getIsAllRowsSelected(),
                          indeterminate: table.getIsSomeRowsSelected(),
                          onChange: () => {},
                        }}
                        readOnly={true}
                      />
                    </th>
                  )}
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        position: "relative",
                        width: header.getSize(),
                        // width: header.column.columnDef.width,
                        // width: "100px",
                        opacity: header.column.columnDef.isDisabled ? 0.33 : 1,
                        // overflowWrap: "break-word",
                        wordBreak: "break-word",

                        whiteSpace: "normal",
                      }}
                    >
                      <div className="row gap5 aic jcsa">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.columnDef.toggled && !kpReadOnly && (
                          <IconButton
                            nameIcon={ICON_NAMES.EYE}
                            tooltipMessage="hide / show the column"
                            widthIcon="1rem"
                            onClick={() => {
                              toggleColumn(header);
                            }}
                            draggable="false"
                          />
                          // <img
                          //   src={header.column.columnDef.isDisabled ? closeEye : openEye}
                          //   alt=""
                          //   style={{width: "1.2em", height: "1em", cursor: "pointer"}}
                          //   onClick={() => toggleColumn(header)}
                          //   draggable="false"
                          // />
                        )}
                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                          ></div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="lines">
              {table.getRowModel().rows.map((row, index) => (
                <KpFinTableRow
                  index={index}
                  key={row.id}
                  row={row}
                  reorderRow={reorderRow}
                  onClick={() => handleRowClick(row)}
                  columns={table.getHeaderGroups()[0].headers}
                  selectedRow={selectedRow}
                  shouldScrollToBottom={shouldScrollToBottom}
                  className={index % 2 === 0 ? "row-even" : "row-odd"}
                  moveRows={moveRows}
                  rowCheck={rowCheck}
                  hasRejectedSkus={hasRejectedSkus}
                  kpReadOnly={kpReadOnly}
                />
              ))}

              {!onlySkuView && (
                <>
                  {/* <
                  className="fcCol aife"
                  style={{
                    position: "sticky",
                    left:
                      !isForTemplates && !isForRequest && !isForStockPool ? "calc(100% - 270px)" : "calc(100% - 210px)",
                    width: "max-content",
                  }}
                > */}
                  {(isForTemplates ? SKU_PLAN_LIST.discountSum : COF.discountSum) > 0 && (
                    <tr style={{backgroundColor: "white", fontWeight: "bold"}}>
                      <td colSpan={columns.length + 1}>
                        <div style={{display: "flex", justifyContent: "end"}}>
                          <div className={`${hiddenRows?.includes("resultSumWithoutDiscount") ? "blur" : ""}`}>
                            {t("SKU sum without discount")}{" "}
                            {moneyFormat(
                              isForTemplates
                                ? SKU_PLAN_LIST.totalSumWithoutDiscount
                                : isForRequest
                                ? REQUEST.totalSumWithoutDiscount
                                : isForStockPool
                                ? SP.totalSumWithoutDiscount
                                : COF.totalSumWithoutDiscount,
                            )}
                          </div>
                          {!isForTemplates && !isForRequest && !isForStockPool && !kpReadOnly && (
                            <ImageButton
                              src={hiddenRows?.includes("resultSumWithoutDiscount") ? closeEye : openEye}
                              onClick={() => onToggleShowRowBtnClick("resultSumWithoutDiscount")}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  {(isForTemplates ? SKU_PLAN_LIST.discountSum : COF.discountSum) > 0 && (
                    <tr style={{backgroundColor: "white", fontWeight: "bold"}}>
                      <td colSpan={columns.length + 1}>
                        <div style={{display: "flex", justifyContent: "end"}}>
                          <div className={`${hiddenRows?.includes("discountSum") ? "blur" : ""}`}>
                            {t("Discount sum")}{" "}
                            {moneyFormat(
                              isForTemplates
                                ? SKU_PLAN_LIST.discountSum
                                : isForRequest
                                ? REQUEST.discountSum
                                : isForStockPool
                                ? SP.discountSum
                                : COF.discountSum,
                            )}
                          </div>
                          {!isForTemplates && !isForRequest && !isForStockPool && !kpReadOnly && (
                            <ImageButton
                              src={hiddenRows?.includes("discountSum") ? closeEye : openEye}
                              onClick={() => onToggleShowRowBtnClick("discountSum")}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  {showPaymentSummaryRows && (
                    <tr style={{backgroundColor: "white", fontWeight: "bold"}}>
                      <td colSpan={columns.length + 1}>
                        <div style={{display: "flex", justifyContent: "end"}}>
                          <div className={`${hiddenRows?.includes("totalSumWithDiscount") ? "blur" : ""}`}>
                            {t("SKU sum to be paid")}{" "}
                            {moneyFormat(
                              isForTemplates
                                ? SKU_PLAN_LIST.totalSumWithDiscounts
                                : isForRequest
                                ? REQUEST.totalSumWithDiscount
                                : isForStockPool
                                ? SP.totalSumWithDiscount
                                : COF.totalSumWithDiscount,
                            )}
                          </div>
                          {!isForTemplates && !isForRequest && !isForStockPool && !kpReadOnly && (
                            <ImageButton
                              src={hiddenRows?.includes("totalSumWithDiscount") ? closeEye : openEye}
                              onClick={() => onToggleShowRowBtnClick("totalSumWithDiscount")}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  {services?.length > 0 &&
                    services.map(({serviceId, type, serviceCost, sumAfterDiscount, nameUkr, nameEng}) => (
                      <tr key={serviceId} style={{backgroundColor: "white", fontWeight: "bold"}}>
                        <td colSpan={columns.length + 1}>
                          <div style={{display: "flex", justifyContent: "end"}}>
                            <div className={`${hiddenRows?.includes("services") ? "blur" : ""}`}>
                              {type
                                ? type?.[language === "en" ? "nameEng" : "nameUkr"]
                                : language === "en"
                                ? nameEng
                                : nameUkr}{" "}
                              {moneyFormat(serviceCost ?? sumAfterDiscount)}
                            </div>
                            {!isForTemplates && !isForRequest && !isForStockPool && !kpReadOnly && (
                              <ImageButton
                                src={hiddenRows?.includes("services") ? closeEye : openEye}
                                onClick={() => onToggleShowRowBtnClick("services")}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  {showPaymentSummaryRows && (
                    <tr
                      className={clsx(!isForTemplates && !isForRequest && !isForStockPool && "pr5")}
                      style={{position: "sticky", bottom: 20, zIndex: 10, backgroundColor: "white", fontWeight: "bold"}}
                    >
                      <td colSpan={columns.length + 1}>
                        <div
                          style={{display: "flex", justifyContent: "end"}}
                          // className={`${hiddenRows?.includes("resultSum") ? "blur" : ""}`}
                        >
                          {t("Result sum to pay")}{" "}
                          {moneyFormat(
                            isForTemplates
                              ? SKU_PLAN_LIST.resultSumWithDiscountsAndServices
                              : isForRequest
                              ? REQUEST.total
                              : isForStockPool
                              ? SP.total
                              : COF.sumPlan,
                          )}
                          {/* <ImageButton src={openEye} onClick={() => onToggleShowRowBtnClick("resultSum")} /> */}
                        </div>
                      </td>
                    </tr>
                  )}
                  {!isForRequest && !isForStockPool ? (
                    <tr
                      style={{position: "sticky", bottom: 0, zIndex: 10, backgroundColor: "white", fontWeight: "bold"}}
                    >
                      <td colSpan={columns.length + 1}>
                        <div style={{display: "flex", justifyContent: "end"}}>
                          <div className={`${hiddenRows?.includes("resultAdvanceSum") ? "blur" : ""}`}>
                            {!advanceForServices
                              ? t("Amount of advance payment due under the COf SKU")
                              : t("Amount of advance payment due across the entire COf")}{" "}
                            {moneyFormat(
                              isForTemplates
                                ? SKU_PLAN_LIST.resultAdvanceSum
                                : isForRequest
                                ? REQUEST.requestAdvance
                                : COF.skuPlanAdvance,
                            )}
                          </div>
                          {!isForTemplates && !isForRequest && !isForStockPool && !kpReadOnly && (
                            <ImageButton
                              src={hiddenRows?.includes("resultAdvanceSum") ? closeEye : openEye}
                              onClick={() => onToggleShowRowBtnClick("resultAdvanceSum")}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    ""
                  )}
                </>
              )}
            </tbody>
          </table>
        </DndProvider>
      </div>
    );
  };

  return {
    KpFinTableRender,
    disabledColls,
    data,
    checkCollWidth,
    generateDataForExport,
    hiddenColumnsString,
    hiddenRows,
  };
};

export default useKpFinTableService;
